import type { Logger } from "@expert/logging";

import { useGuideSession } from "@expert/solve-tools";
import { type ReactNode, useCallback, useEffect, useRef } from "react";
import { useShallow } from "zustand/react/shallow";

import type { TabOption } from "@/panels/modules/panels/store/panels.store.types";

import { usePlatformGuideManager } from "@/guides";
import { usePanelsStore } from "@/panels/modules/panels/store/panels.store";
import { ProductSaleFlowPanel } from "@/sales-tools/product-sale-flow/ProductSaleFlowPanel";
import { useProductSaleFlow } from "@/sales-tools/product-sale-flow/useProductSaleFlow";

import { GuidedFlowsPanel } from "../GuidedFlowsPanel";

type UseWorkspaceContentPanelsParams = {
    customerSession: boolean;
    isLargeScreen: boolean;
    logger: Logger;
    sessionId: string;
};

type FlowSlots = {
    leftTopBSlot: ReactNode;
    leftBottomBSlot: ReactNode;
    rightTopASlot: ReactNode;
    rightBottomASlot: ReactNode;
};

export const useWorkspaceContentPanels = ({
    customerSession,
    isLargeScreen,
    logger,
    sessionId,
}: UseWorkspaceContentPanelsParams) => {
    const { selectedProduct, productSaleFlowPanelTitle, handleProductSaleClose } = useProductSaleFlow();
    const {
        leftTopSelectedTab,
        leftTopTabBIsMinimized,
        leftBottomTabBIsMinimized,
        rightTopTabAIsMinimized,
        rightBottomTabAIsMinimized,
    } = usePanelsStore(
        useShallow((s) => ({
            leftTopSelectedTab: s.leftTop.selectedTab,
            leftTopTabBIsMinimized: s.leftTop.b.isMinimized,
            leftBottomTabBIsMinimized: s.leftBottom.b.isMinimized,
            rightTopTabAIsMinimized: s.rightTop.a.isMinimized,
            rightBottomTabAIsMinimized: s.rightBottom.a.isMinimized,
        })),
    );
    const { currentGuideName, openGuideSource } = useGuideSession(sessionId);

    const { areGuidesAvailable, guide, guideToolboxCommand, isGuideOpen, closeGuide } = usePlatformGuideManager({
        isCustomerSession: customerSession,
        sessionId,
        logger,
        areGuidedFlowsAllowed: true,
    });

    const isSmallScreen = !isLargeScreen;
    const hasProductFlow = Boolean(selectedProduct);
    const hasGuideFlow = areGuidesAvailable && isGuideOpen;

    const showGuideInLeftTopB = isSmallScreen && hasGuideFlow;
    const showProductInLeftBottomB = isSmallScreen && hasProductFlow;
    const keepLeftTopBTabVisible = showProductInLeftBottomB;
    const showGuideInRightTopA = isLargeScreen && areGuidesAvailable;
    const showProductInRightBottomA = isLargeScreen && hasProductFlow;

    const wasGuideOpenRef = useRef(isGuideOpen);
    const onGuideClose = useCallback(() => closeGuide("closeButton"), [closeGuide]);

    const flowSlots = resolveFlowSlots({
        guide,
        keepLeftTopBTabVisible,
        showGuideInLeftTopB,
        showGuideInRightTopA,
        showProductInLeftBottomB,
        showProductInRightBottomA,
    });
    const rightTopConfig = getRightTopConfig({
        currentGuideName,
        isGuideOpen,
        isMinimized: rightTopTabAIsMinimized,
        onGuideClose,
        showGuideInRightTopA,
    });
    const leftBottomConfig = getLeftBottomConfig({
        handleProductSaleClose,
        isSmallScreen,
        leftBottomTabBIsMinimized,
        leftTopSelectedTab,
        productSaleFlowPanelTitle,
        showProductInLeftBottomB,
    });
    const leftTopConfig = getLeftTopConfig({
        currentGuideName,
        handleProductSaleClose,
        keepLeftTopBTabVisible,
        leftTopSelectedTab,
        leftTopTabBIsMinimized,
        onGuideClose,
        productSaleFlowPanelTitle,
        showGuideInLeftTopB,
    });
    const rightBottomConfig = {
        selectedTab: "a" as const,
        a: {
            headerTitle: productSaleFlowPanelTitle,
            tabTitle: productSaleFlowPanelTitle,
            isMinimized: rightBottomTabAIsMinimized,
            isClosed: false,
            showHeader: true,
            onClose: handleProductSaleClose,
        },
    };

    // On resize, config sync may overwrite left-side isMinimized with layout
    // defaults. The right side always reflects the store value, so copy it back.
    useEffect(() => {
        const { setIsMinimized } = usePanelsStore.getState();
        setIsMinimized("leftTop")("b")(usePanelsStore.getState().rightTop.a.isMinimized);
        setIsMinimized("leftBottom")("b")(usePanelsStore.getState().rightBottom.a.isMinimized);
    }, [isLargeScreen]);

    // Expand and select the guide panel on open, resize, or re-launch.
    useEffect(() => {
        const { setIsMinimized, setSelectedTab } = usePanelsStore.getState();

        const wasOpen = wasGuideOpenRef.current;
        wasGuideOpenRef.current = isGuideOpen;

        if (!areGuidesAvailable || !isGuideOpen) return;

        // Expand on first open — store mirror syncs the counterpart.
        if (!wasOpen) {
            setIsMinimized("leftTop")("b")(false);
        }

        // Select the guide tab. leftBottom is handled by the product flow effect.
        if (isLargeScreen) {
            setSelectedTab("rightTop")("a");
        } else {
            setSelectedTab("leftTop")("b");
        }
    }, [areGuidesAvailable, isGuideOpen, isLargeScreen, openGuideSource]);

    // Select the product flow tab when a product is activated or screen resizes.
    useEffect(() => {
        if (!selectedProduct) return;

        const { setSelectedTab } = usePanelsStore.getState();
        if (isLargeScreen) {
            setSelectedTab("rightBottom")("a");
        } else {
            setSelectedTab("leftTop")("b");
            setSelectedTab("leftBottom")("b");
        }
    }, [isLargeScreen, selectedProduct]);

    return {
        flowSlots,
        guideToolboxCommand,
        leftBottomConfig,
        leftTopConfig,
        rightBottomConfig,
        rightTopConfig,
    };
};

// Slots must be null (not a component returning null) for Panels to treat them as empty.
const resolveFlowSlots = ({
    guide,
    keepLeftTopBTabVisible,
    showGuideInLeftTopB,
    showGuideInRightTopA,
    showProductInLeftBottomB,
    showProductInRightBottomA,
}: {
    guide: ReactNode;
    keepLeftTopBTabVisible: boolean;
    showGuideInLeftTopB: boolean;
    showGuideInRightTopA: boolean;
    showProductInLeftBottomB: boolean;
    showProductInRightBottomA: boolean;
}): FlowSlots => {
    const leftTopBSlot = showGuideInLeftTopB ? (
        <GuidedFlowsPanel guide={guide} />
    ) : keepLeftTopBTabVisible ? (
        // Keep tab "b" visible so the product flow tab bar renders in leftBottom.
        <TabBPresenceMarker />
    ) : null;

    return {
        leftTopBSlot,
        leftBottomBSlot: showProductInLeftBottomB ? <ProductSaleFlowPanel /> : null,
        rightTopASlot: showGuideInRightTopA ? <GuidedFlowsPanel guide={guide} /> : null,
        rightBottomASlot: showProductInRightBottomA ? <ProductSaleFlowPanel /> : null,
    };
};

export const getRightTopConfig = ({
    currentGuideName,
    isGuideOpen,
    isMinimized,
    onGuideClose,
    showGuideInRightTopA,
}: {
    currentGuideName: string | undefined;
    isGuideOpen: boolean;
    isMinimized: boolean;
    onGuideClose: () => void;
    showGuideInRightTopA: boolean;
}) => {
    if (!showGuideInRightTopA) return undefined;

    return {
        selectedTab: "a" as const,
        a: {
            headerTitle: currentGuideName ?? "Guided Flows",
            tabTitle: "Guided Flows",
            isMinimized,
            isClosed: !isGuideOpen,
            showHeader: true,
            onClose: onGuideClose,
        },
    };
};

export const getLeftBottomConfig = ({
    handleProductSaleClose,
    isSmallScreen,
    leftBottomTabBIsMinimized,
    leftTopSelectedTab,
    productSaleFlowPanelTitle,
    showProductInLeftBottomB,
}: {
    handleProductSaleClose: () => void;
    isSmallScreen: boolean;
    leftBottomTabBIsMinimized: boolean;
    leftTopSelectedTab: TabOption;
    productSaleFlowPanelTitle: string;
    showProductInLeftBottomB: boolean;
}) => {
    const isProductTabVisible = isSmallScreen && showProductInLeftBottomB && leftTopSelectedTab !== "a";

    return {
        selectedTab: (isProductTabVisible ? "b" : "a") as TabOption,
        a: {
            headerTitle: "",
            tabTitle: "",
            isMinimized: false,
            isClosed: true,
            showHeader: false,
        },
        b: {
            headerTitle: productSaleFlowPanelTitle,
            tabTitle: "Flows",
            isMinimized: isProductTabVisible ? leftBottomTabBIsMinimized : false,
            isClosed: !isProductTabVisible,
            showHeader: isProductTabVisible,
            onClose: handleProductSaleClose,
        },
    };
};

export const getLeftTopConfig = ({
    currentGuideName,
    handleProductSaleClose,
    keepLeftTopBTabVisible,
    leftTopSelectedTab,
    leftTopTabBIsMinimized,
    onGuideClose,
    productSaleFlowPanelTitle,
    showGuideInLeftTopB,
}: {
    currentGuideName: string | undefined;
    handleProductSaleClose: () => void;
    keepLeftTopBTabVisible: boolean;
    leftTopSelectedTab: TabOption;
    leftTopTabBIsMinimized: boolean;
    onGuideClose: () => void;
    productSaleFlowPanelTitle: string;
    showGuideInLeftTopB: boolean;
}) => {
    return {
        selectedTab: leftTopSelectedTab,
        a: {
            headerTitle: "Expert Assist",
            tabTitle: "Assist",
            isMinimized: false,
            isClosed: false,
            showHeader: false,
        },
        b: {
            headerTitle: showGuideInLeftTopB ? (currentGuideName ?? "Guided Flows") : productSaleFlowPanelTitle,
            tabTitle: "Flows",
            isMinimized: showGuideInLeftTopB ? leftTopTabBIsMinimized : keepLeftTopBTabVisible,
            isClosed: !showGuideInLeftTopB && !keepLeftTopBTabVisible,
            showHeader: showGuideInLeftTopB,
            onClose: showGuideInLeftTopB ? onGuideClose : handleProductSaleClose,
        },
    };
};

const TabBPresenceMarker = () => null;
